import '../../stylesheets/dashboards/dashboards.css'
import '../../stylesheets/dashboards/football-field.css'
import { useEffect, useRef, useState }                  from 'react'
import { useOutletContext }                             from 'react-router-dom'
import ChartsSkeleton                                   from '../skeletons/ChartsSkeleton.js'
import Chart                                            from 'chart.js/auto';
import { formatNumber }                                 from '../../utilities/formatNumber';
import { filterManyByIds, findOneById }                 from '../../utilities/idResolution';
import { usePublicAPI }                                 from '../../hooks/external/proprietary/usePublicAPI.js';
import dashboardStyles                                  from '../../stylesheets/dashboards/dashboards.module.css';
import ffStyles                                         from '../../stylesheets/dashboards/football-field.module.css';
import { 
    resolve52WeekHighLow,
    resolveChoosenMultField, 
    getSharePriceRange, 
    resolveAxisMaxY, 
    resolveAxisMinY 
}                                                       from '../../services/football-field/main.js';
import { COMPS_MULT_OPTIONS, COMPS_DEFAULT_MULT }       from '../../_constants/public-comparables'
import { TRANS_MULT_OPTIONS, TRANS_DEFAULT_MULT }       from '../../_constants/transaction-comps'

const FootballField = () => {
    const { getCoLogo } = usePublicAPI();
    const {
        // Public Comps (Read Only)
        companies,
        selectedCompanyIds,
        compsTiles,
        selectedCompsTileIds,
        // Transactions (Read Only)
        transactions,
        selectedTransactionIds,
        transTiles,
        selectedTransTileIds,
        // Lifted from Football Field
        selectedFootballFieldCoId,
        onSelectedFootballFieldCoIdChange,
        // Loading states
        compsIsLoading,
        transactionsIsLoading
    } = useOutletContext()

    // State management
    const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);
    const [searchTerm, setSearchTerm]           = useState('');

    const chartRef                              = useRef(null);
    const chartInstanceRef                      = useRef(null);

    // ===== Chart Configuration ===== //
    useEffect(() => {
        if (chartRef.current) {
            const ctx = chartRef.current.getContext('2d');

            // Destroy existing chart if it exists
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }

            // Create floating bars data - each bar shows from low to high
            const floatingBarsData = skeletonBars.map(bar => [bar.low, bar.high]);

            // Create new chart
            chartInstanceRef.current = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: skeletonBars.map(bar => bar.label),
                    datasets: [
                        {
                            label: 'Valuation Range',
                            data: floatingBarsData,
                            backgroundColor: skeletonBars.map((_, index) => {
                                const colors = ['#004A7C', '#006A9C', '#0088CC', '#33A3D1'];
                                return colors[index % colors.length];
                            }),
                            borderWidth: 0,
                            borderRadius: {
                                topLeft: 4,
                                topRight: 4,
                                bottomLeft: 4,
                                bottomRight: 4
                            },
                            borderSkipped: false,
                            maxBarThickness: 150,
                            order: 2
                        }, 
                        {
                            label: 'Benchmark',
                            type: 'line',
                            data: Array(skeletonBars.length).fill(benchmarkValue),
                            borderColor: 'rgba(70, 223, 83, 0.8)',
                            borderWidth: 3,
                            pointRadius: 0,
                            pointHoverRadius: 0,
                            order: 1
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: false
                        },
                        tooltip: {
                            enabled: false
                        },
                        datalabels: {
                            display: false  // We'll use afterDatasetsDraw to draw custom labels
                        }
                    },
                    scales: {
                        x: {
                            grid: {
                                display: false
                            },
                            ticks: {
                                color: '#34495e',
                                font: {
                                    size: 12
                                },
                                padding: 10,
                                // ** X-Axis Label Responsiveness Controls in Config ** //
                                autoSkip: false,   // never drop an x-axis label (thin them out), even when they start to collide
                                minRotation: 0,    // flat (horizontal) when available width for entire label text
                                maxRotation: 90    // allow label to rotate all the way to precisely vertical (max 'thin-ness') as available width for label text shrinks with viewport width shrinking                               
                            }
                        },
                        y: {
                            min: yAxisMin,
                            max: yAxisMax,
                            ticks: {
                                color: '#34495e',
                                font: {
                                    size: 12
                                },
                                precision: 0,
                                // NOTE: This callback determines which tick INDICES to display (first, middle, last), not the tick values themselves
                                // Chart.js auto-calculates the tick values; we just selectively show 3 of them
                                callback: function(value, index, ticks) {
                                    const firstTick  = index === 0
                                    const middleTick = index === (Math.floor(ticks.length/2))
                                    const lastTick   = index === ticks.length - 1

                                    if (firstTick || middleTick || lastTick) {
                                        return '$' + formatNumber(value);
                                    }
                                    return ""
                                }
                            },
                            grid: {
                                display: false
                            }
                        }
                    },
                    animation: {
                        duration: 0
                    }
                },
                plugins: [{
                    id: 'customLabels',
                    afterDatasetsDraw: function(chart) {
                        const ctx = chart.ctx;
                        const meta = chart.getDatasetMeta(0);

                        ctx.save();
                        ctx.font = '12px Arial';
                        ctx.fillStyle = '#2c3e50';
                        ctx.textAlign = 'center';

                        meta.data.forEach((bar, index) => {
                            const data = skeletonBars[index];

                            // bar.x:    the horizontal pixel center of the bar (used to center the label text in the middle of the bar width)
                            // bar.base: the pixel of the bar's baseline-side edge:    **THE END NEAREST $0**,        where the bar "starts growing from either up or down"
                            // bar.y:    the pixel of the bar's value/tip edge:        **THE END FARTHEEST FROM $0**, where the bar "reaches to in either direction either up or down"
                                // therefore the value of bar.base in positive land is always less than the value of bar.y (pixels go from top to bottom and bases is 'below' y)                            
                                // and the value of bar.base is negative land is always greater than that of the value of bar.y (because again pixels from top to bottom and now base it 'above' y)
                            const x = bar.x;
                            ctx.fillText('$' + formatNumber(data.high.toFixed(2)), x, Math.min(bar.y, bar.base) - 8  );
                            ctx.fillText('$' + formatNumber(data.low.toFixed(2)),  x, Math.max(bar.y, bar.base) + 18 );
                        });

                        ctx.restore();
                    }
                }]
            });
        }

        // Cleanup function
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [selectedFootballFieldCoId]);

    if (compsIsLoading || transactionsIsLoading) return <ChartsSkeleton />

    const selectedCoDataObj         = findOneById(companies, selectedFootballFieldCoId)
    console.log("=================================")
    console.log(`[(${selectedFootballFieldCoId}, ${selectedCoDataObj.ticker}) DATA OBJECT]: `, selectedCoDataObj)
    
    // ==== 52 Week High / Low Inputs ==== //
    const { fiftyTwoWeekHigh, fiftyTwoWeekLow } = resolve52WeekHighLow(selectedCoDataObj)

    // ==== Comps Inputs ==== //
    // Comps Mult Name
    // Valuation Metric Resolution Algo:
        // a) if no valuation metric tiles (which includes no tiles at all) -> use default valuation metric
        // b) if valuation metric tile(s)                                   -> find first against schema and check to see if multiple is N/A for selected company -> if N/A, use default valuation metric
        // c) if pass both of these                                         -> find first valuation metric tile and use for share price conversion math
    const compsMultFieldChoosen                  = resolveChoosenMultField(compsTiles, selectedCompsTileIds, COMPS_MULT_OPTIONS, COMPS_DEFAULT_MULT, selectedCoDataObj)    
    console.log("[COMPS MULT FIELD IN COMPONENT]: ", compsMultFieldChoosen)
    // Comps Share Price Range (using 'name: alias' object destructuring syntax technique, Step 1: Destructuring: allows us to pull the name out of what is returned (destructuring by matching the name in the object returned by function) + b) alialize: and alialize it to unique declaration from generic declaration (generic returned in object from service so that the service can be reusable) based on if its comps or trans!
    const { SPHigh: compsSPHigh, SPLow: compsSPLow, multHigh: compsMultHigh, multLow: compsMultLow } = getSharePriceRange(companies, selectedCompanyIds, compsMultFieldChoosen, selectedCoDataObj)

    // ==== Transaction Inputs ==== //
    // Transactions Mult Name
    const transMultFieldChoosen                  = resolveChoosenMultField(transTiles, selectedTransTileIds, TRANS_MULT_OPTIONS, TRANS_DEFAULT_MULT, selectedCoDataObj)
    console.log("[TRANS MULT FIELD IN COMPONENT]: ", compsMultFieldChoosen)
    // Transactions Share Price Range (using 'name: alias' object destructuring syntax technique, Step 1: Destructuring: allows us to pull the name out of what is returned (destructuring by matching the name in the object returned by function) + b) alialize: and alialize it to unique declaration from generic declaration (generic returned in object from service so that the service can be reusable) based on if its comps or trans!
    const { SPHigh: transSPHigh, SPLow: transSPLow, multHigh: transMultHigh, multLow: transMultLow } = getSharePriceRange(transactions, selectedTransactionIds, transMultFieldChoosen, selectedCoDataObj) 


    // ========= For Right Panel ========= //
    const skeletonBars = [
        { label: '52-Week Range', high: fiftyTwoWeekHigh, low: fiftyTwoWeekLow },
        { label: 'Public Comparables', high: compsSPHigh, low: compsSPLow },
        { label: 'Precedent Transactions', high: transSPHigh, low: transSPLow },
        //{ label: 'DCF', high: 24, low: 18 }
    ];

    // Benchmark value (e.g., current stock price)
    const benchmarkValue = selectedCoDataObj.sharePrice // find co from companies based on coId and then dot notation into its share price for the value of this;

    // Dynamic y-axis calculation
    const yAxisMax = resolveAxisMaxY(skeletonBars)
    const yAxisMin = resolveAxisMinY(skeletonBars)
    console.log("y axis max:", yAxisMax)
    console.log("y axis min:", yAxisMin)

    // ===== For Left Panel ===== //
    // Peak market multiples
    const marketMultiples = {
        ttmRevenueMultiple:             { peak: 8.1,   current: 9.8,   label: "LTM Revenue Multiple"       },
        currentYearRevenueMultiple:     { peak: 7.4,   current: 9.1,   label: "Current Revenue Multiple"   },
        nextYearRevenueMultiple:        { peak: 6.7,   current: 8.3,   label: "Forward Revenue Multiple"   },
        ttmPeMultiple:                  { peak: 74.2,  current: 37.8,  label: "LTM PE"                     },
        currentYearPeMultiple:          { peak: 50.5,  current: 30.5,  label: "Current PE"                 },
        nextYearPeMultiple:             { peak: 44.7,  current: 29.8,  label: "Forward PE"                 },
        ttmEbitdaMultiple:              { peak: 44.5,  current: 24.2,  label: "LTM EBITDA Multiple"        },
        //currentYearEbitdaMultiple:      { peak: 38.1,  current: 20.5,  label: "Current PE"                 },
        //nextYearEbitdaMultiple:         { peak: 32.5,  current: 19.2,  label: "Forward PE"                 },
    }
    
    const companyValue = selectedCoDataObj[compsMultFieldChoosen]
    
    const currentValue = marketMultiples[compsMultFieldChoosen].current
    const peakValue    = marketMultiples[compsMultFieldChoosen].peak
    const label        = marketMultiples[compsMultFieldChoosen].label

    const summaryStats = {
        publicCompsMultiples: { min: compsMultLow, max: compsMultHigh },
        // growth: { min: 11.2, max: 45.6 },
        precedentMultiples: { min: transMultLow, max: transMultHigh },
        // dealSize: { min: 2.87, max: 5.09 }
    };    

    // ===== Handlers For Offcanvas ===== //
    // company selection
    const selectCompany = (companyId) => {
        onSelectedFootballFieldCoIdChange(companyId);
        setIsOffcanvasOpen(false); // Auto-close on selection
        console.log(`[SUCCESS]: Company selected for Football Field: ${companyId}`)
    };
    // company search
    const filteredCompanies = companies.filter(company => 
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.ticker.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
    <div className={dashboardStyles['page-wrapper']}>
        <div className={dashboardStyles['dashboard']}>

            {/* Left Panel - Controller */}
            <div className={dashboardStyles['left-panel']}>
                
                {/* Header Section */}
                <div className={dashboardStyles['left-panel-header-container']}>                
                    <h2 className={dashboardStyles['panel-title']}>Valuation Metrics</h2>
                </div>
                
                {/* Body Section: Metric Area */}
                {/* Body Section 1: Market Peak Multiples */}
                <div className={ffStyles['section-one-container']}>
                    <div className={ffStyles['ff-market-multiples-section']}>
                        {/* Body Header */}
                        <h3 className={ffStyles['ff-section-title']}>Dot-Com Market Peak vs. Current</h3>
                        
                        {/* Body Content Cards */}
                        <div className={ffStyles['ff-metric-row']}>
                            <div className={ffStyles['ff-metric-content']}>
                                <span className={ffStyles['ff-metric-label']}>
                                    Dot-Com Peak S&P 500<br />
                                    {label}:
                                </span>
                                <span className={ffStyles['ff-metric-value']}> ~ {peakValue}x </span>
                            </div>
                        </div>    
                        
                        <div className={ffStyles['ff-metric-row']}>
                            <div className={ffStyles['ff-metric-content']}>
                                <span className={ffStyles['ff-metric-label']}>
                                    09/01/2026 S&P 500<br />
                                    {label}:
                                </span>
                                <span className={ffStyles['ff-metric-value']}> ~ {currentValue}x </span>
                            </div>
                        </div>                        

                        <div className={ffStyles['ff-metric-row']}>
                            <div className={ffStyles['ff-metric-content']}>
                                <span className={ffStyles['ff-metric-label']}>
                                    {selectedCoDataObj.name}<br />
                                    {label}:
                                </span>
                                <span className={ffStyles['ff-metric-value']}> ~ {companyValue.toFixed(1)}x </span>
                            </div>
                        </div>

                        <div className={ffStyles['ff-metric-row']}>
                            <div className={ffStyles['ff-metric-content']}>
                                <span className={ffStyles['ff-metric-label']}>
                                    Selected Comps <br />
                                    {label} Range:
                                </span>
                                <span className={ffStyles['ff-metric-value']}>{summaryStats.publicCompsMultiples.max.toFixed(1)}x - {summaryStats.publicCompsMultiples.min.toFixed(1)}x</span>
                            </div>
                        </div>                                            

                    </div>
                </div>

            </div>
                    
                {/* Right Panel - Chart(s) */}
                <div className={dashboardStyles['right-panel']}>
                    {/* Header Section */}
                    <div className="right-panel-header-container">
                        <h2 className="panel-title">Company Football Field</h2>
                        <div className="right-panel-football-field-controls-container">
                            <button
                                className="selector-btn"
                                onClick={() => setIsOffcanvasOpen(true)}
                            >
                                <svg className="edit-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                Edit Selection
                            </button>
                        </div>
                    </div>
                    {/* Body (charting) Section */}   
                    <div className={dashboardStyles['charts-container']}>
                        <div className={dashboardStyles['chart-section']}>
                            {/* Header Section / Chart Title */}
                            <div className="chart-header-container">
                                <img
                                    className="chart-header-logo"
                                    src={getCoLogo(selectedCoDataObj.ticker)}
                                    alt={selectedCoDataObj.name}

                                />
                                <h2 className="chart-header-text">
                                    {companies.find(c => c.id === selectedFootballFieldCoId)?.name} Relative Valuation
                                </h2>
                            </div>
                            {/* Benchmark Label (Current Share Price) */}
                            <div className={ffStyles['ff-benchmark-label']}>
                                <span>Current Share Price:</span>
                                <span className={ffStyles['ff-benchmark-price']}>${formatNumber(benchmarkValue)}</span>
                            </div>
                            {/* Chart */}
                            <div style={{
                                position: 'relative',
                                height: '400px',
                                width: '100%'
                            }}>
                                <canvas ref={chartRef}></canvas>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Off Canvas Section */}               
                {/* Offcanvas Overlay */}
                <div 
                    className={`offcanvas-overlay ${isOffcanvasOpen ? 'open' : ''}`}
                    onClick={() => setIsOffcanvasOpen(false)}
                ></div>

                {/* Offcanvas Panel */}
                <div className={`offcanvas ${isOffcanvasOpen ? 'open' : ''}`}>

                    {/* Header */}
                    <div className="offcanvas-header">
                        <h3 className="offcanvas-title">Company Selector</h3>
                        <button 
                            className="close-btn"
                            onClick={() => setIsOffcanvasOpen(false)}
                        >
                            ×
                        </button>
                    </div>

                    {/* Body */}
                    <div className="offcanvas-body">

                        {/* Search */}
                        <div className="search-container">
                            <div className="search-input-wrapper">
                                <div className="search-content">
                                    <span className="search-label">Company Search</span>
                                    <input
                                        type="text"
                                        className="search-input-field"
                                        placeholder="Search sector for company"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                            
                        {/* Selected Company */}
                        <div className="selected-companies-section">
                            <h4 className="selected-companies-title">Current Company Being Valued </h4>
                            <div className="selected-companies-grid-container">
                                <div className="selected-company-logo-card">
                                    <img
                                        src={getCoLogo(selectedCoDataObj.ticker)}
                                        alt={selectedCoDataObj.name}
                                        className="logo-card-company-logo"
                                    />
                                    <div className="selected-company-info">
                                        <span className="logo-card-company-name">{selectedCoDataObj.name}</span>
                                        <span className="logo-card-company-ticker">({selectedCoDataObj.ticker})</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Available Companies to Select Grid */}
                        <div className="available-companies-grid-container">
                            {filteredCompanies.map(company => (
                                <div
                                    key={company.id}
                                    className={`available-company-logo-card ${
                                        selectedFootballFieldCoId === company.id ? 'selected' : ''
                                    }`}
                                    onClick={() => selectCompany(company.id)}
                                >
                                    <img
                                        src={getCoLogo(company.ticker)}
                                        alt={company.name}
                                        className="logo-card-company-logo"
                                    />
                                    <h4 className="logo-card-company-name">{company.name}</h4>
                                    <p className="logo-card-company-ticker">({company.ticker})</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
        </div>
    </div>
    );
};

export default FootballField;